/**
 * Shipping with a carrier (owner 2026-10-31; shop spec 11): the rules, pure.
 *
 * The mosque's shop sends an order with Helthjem or PostNord (or «another way»; Posten/Bring is paused, see OFFERED),
 * at a price the mosque sets itself per carrier and weight band («PostNord inntil 2 kg: 99 kr»). The API (apps/api/src/store-orders.js
 * and apps/api/src/shipping/) reads and writes; this decides:
 *
 *   CARRIERS / label(carrier, lang)        the four carriers and their names (nb/en; ur reads en)
 *   trackingUrl(carrier, nr)               the public tracking page for a number, per carrier, or null for «other»
 *   validTrackingNumber(nr)                loose: 8–40 letters and digits (a 13-character UPU code, a 17–20 digit parcel id)
 *   settings(input)                        shipping_mode (flat | carrier) and the enabled carriers, as staff set them
 *   rates(input)                           the rate table as staff send it, checked and sorted
 *   weightOf(lines, products)              the basket's weight in grams (an unknown product weight counts DEFAULT_WEIGHT_G)
 *   rateFor(rates, carrier, weightG)       the price of sending a basket of that weight with that carrier, or null
 *   quotes(rates, carriers, weightG)       one price per enabled carrier that can take the basket — the checkout chips
 *   shipInput(body)                        what staff give when they mark an order sent (carrier, tracking number, url)
 *
 * No I/O, no clock. Nothing here knows a buyer: the address stays sealed in store_order_contact.
 */
const CARRIERS = ['posten', 'helthjem', 'postnord', 'other'];
/**
 * The carriers a mosque may switch on NOW. Posten/Bring is paused (owner 2026-10-31: «we are dropping posten for now»):
 * it stays in CARRIERS (the database CHECK, its name and tracking page), so orders already sent with it keep working and
 * bringing it back is this one line — add 'posten' here — and a deploy of the API and the panel. The app needs nothing:
 * it shows what the server offers. Choices and rates stored before the pause are kept, ignored, and count again then.
 */
const paused = (c) => isCarrier(c) && !OFFERED.includes(c);
const OFFERED = ['helthjem', 'postnord', 'other'];
const isOffered = (c) => OFFERED.includes(c);
/** The stored carriers of a paused kind, kept through a save so that offering them again brings them back. */
function pausedCarriers(text) {
  return CARRIERS.filter((c) => !OFFERED.includes(c) && String(text || '').split(',').map((s) => s.trim()).includes(c));
}
const SHIPPING_MODES = ['flat', 'carrier'];
const LABELS = {
  posten: { no: 'Posten / Bring', en: 'Posten / Bring' },
  helthjem: { no: 'Helthjem', en: 'Helthjem' },
  postnord: { no: 'PostNord', en: 'PostNord' },
  other: { no: 'Annen frakt', en: 'Other carrier' },
};
/**
 * The public tracking page per carrier. The patterns are those the carriers' own pages and shipping notices use
 * (2026-10-31): Posten/Bring sporing.posten.no/sporing/<nr>; PostNord tracking.postnord.com/no/?id=<nr>; Helthjem's
 * page is helthjem.no/sporing — its query name could not be confirmed from the public site (it refuses automated
 * reads), so the number is given as `sporingsnummer`; a wrong name still lands on the tracking page. «other» has no
 * page: staff may paste a full https address instead.
 */
const TRACKING = {
  posten: (nr) => `https://sporing.posten.no/sporing/${encodeURIComponent(nr)}`,
  helthjem: (nr) => `https://helthjem.no/sporing?sporingsnummer=${encodeURIComponent(nr)}`,
  postnord: (nr) => `https://tracking.postnord.com/no/?id=${encodeURIComponent(nr)}`,
};
/** A product with no weight set counts as this much (spec: default 500 g when unknown). */
const DEFAULT_WEIGHT_G = 500;
/** A product weighs at most 50 kg; a weight band at most 100 kg; a basket of 50 × 100 lines is bounded by both. */
const MAX_PRODUCT_WEIGHT_G = 50_000;
const MAX_BAND_WEIGHT_G = 100_000;
/** A rate is 0 to 2 000 kr (the same bound as the flat fee). */
const MAX_RATE_ORE = 200_000;
/** Rate rows per mosque: four carriers × a handful of bands is plenty. */
const MAX_RATES = 40;
const TRACKING_NUMBER = /^[A-Za-z0-9]{8,40}$/;
const MAX_TRACKING_URL = 300;
const MAX_PICKUP_POINT = 120;
const CTRL = /[\u0000-\u001f\u007f]/;
const clean = (v) => (typeof v === 'string' ? v.replace(/\s+/g, ' ').trim() : '');

const isCarrier = (c) => CARRIERS.includes(c);
function label(carrier, lang = 'no') {
  const l = LABELS[carrier];
  if (!l) return null;
  return lang === 'no' ? l.no : l.en;
}

/** The tracking number as it is stored: spaces removed, upper-cased, 8–40 letters and digits — or null. */
function trackingNumber(v) {
  const s = String(v ?? '').replace(/\s/g, '').toUpperCase();
  return TRACKING_NUMBER.test(s) ? s : null;
}
const validTrackingNumber = (v) => trackingNumber(v) !== null;

/** The public tracking page for a number, or null («other», or no number). */
function trackingUrl(carrier, nr) {
  const n = trackingNumber(nr);
  const build = TRACKING[carrier];
  return n && build ? build(n) : null;
}
/** A tracking address staff paste for «other»: https, at most 300 characters, one line — or null. */
function pastedTrackingUrl(v) {
  const s = String(v ?? '').trim();
  if (!s || s.length > MAX_TRACKING_URL || CTRL.test(s) || /\s/.test(s)) return null;
  let u;
  try { u = new URL(s); } catch { return null; }
  return u.protocol === 'https:' ? s : null;
}

/** A product's weight as staff send it: null (unknown → DEFAULT_WEIGHT_G at checkout) or a whole number of grams, 1 … 50 000. */
function validWeightG(v) {
  return v === null || v === undefined || (typeof v === 'number' && Number.isSafeInteger(v) && v >= 1 && v <= MAX_PRODUCT_WEIGHT_G);
}

/**
 * The shipping settings as staff set them (on top of store-checkout settings): `shippingMode` flat (one fee, as
 * before) or carrier (the rate table), and the carriers the mosque uses. In carrier mode at least one carrier.
 * Not sent (a panel from before this): undefined, so what is stored is kept.
 */
function settings(input = {}) {
  const src = input && typeof input === 'object' && !Array.isArray(input) ? input : {};
  const errors = {};
  const out = {};
  if (src.shippingMode !== undefined) {
    if (!SHIPPING_MODES.includes(src.shippingMode)) errors.shippingMode = 'Velg fast frakt eller frakt med transportør.';
    else out.shippingMode = src.shippingMode;
  }
  if (src.shippingCarriers !== undefined) {
    if (!Array.isArray(src.shippingCarriers) || src.shippingCarriers.some((c) => !isCarrier(c))) errors.shippingCarriers = 'Ukjent transportør.';
    else if (src.shippingCarriers.some((c) => !isOffered(c))) errors.shippingCarriers = `${label(src.shippingCarriers.find((c) => !isOffered(c)))} tilbys ikke for øyeblikket. Velg blant ${OFFERED.map((c) => label(c)).join(', ')}.`;
    else out.shippingCarriers = OFFERED.filter((c) => src.shippingCarriers.includes(c));
  }
  if (src.shipping === true && out.shippingMode === 'carrier' && out.shippingCarriers && !out.shippingCarriers.length) errors.shippingCarriers = 'Velg minst én transportør for frakt med transportør.';
  return Object.keys(errors).length ? { ok: false, errors } : { ok: true, value: out };
}

/** `shipping_carriers` as stored (comma-separated) → the list, in catalogue order; unknown names and paused carriers dropped. */
function parseCarriers(text) {
  const set = new Set(String(text || '').split(',').map((s) => s.trim()).filter(Boolean));
  return OFFERED.filter((c) => set.has(c));
}
const formatCarriers = (list) => CARRIERS.filter((c) => (list || []).includes(c)).join(',');

/**
 * The rate table as staff send it: rows of { carrier, maxWeightG, priceOre }. Each carrier's bands must have distinct
 * weights; the rows come back sorted by carrier (catalogue order) and weight. An empty table is allowed (then
 * nothing can be sent in carrier mode — the panel says so).
 */
function rates(input) {
  if (!Array.isArray(input)) return { ok: false, message: 'Send fraktsatsene som en liste.' };
  if (input.length > MAX_RATES) return { ok: false, message: `Høyst ${MAX_RATES} fraktsatser.` };
  const seen = new Set();
  const out = [];
  for (const r of input) {
    const row = r && typeof r === 'object' && !Array.isArray(r) ? r : {};
    if (!isCarrier(row.carrier)) return { ok: false, message: 'Ukjent transportør i fraktsatsene.' };
    if (!isOffered(row.carrier)) return { ok: false, message: `${label(row.carrier)} tilbys ikke for øyeblikket. Fjern satsene for den.` };
    const w = row.maxWeightG;
    if (!(typeof w === 'number' && Number.isSafeInteger(w) && w >= 1 && w <= MAX_BAND_WEIGHT_G)) return { ok: false, message: 'Vekten «inntil» er et helt antall gram fra 1 til 100 000.' };
    const p = row.priceOre;
    if (!(typeof p === 'number' && Number.isSafeInteger(p) && p >= 0 && p <= MAX_RATE_ORE)) return { ok: false, message: 'Fraktprisen er fra 0 til 2 000 kr, i hele øre.' };
    const key = `${row.carrier}:${w}`;
    if (seen.has(key)) return { ok: false, message: 'Samme transportør og vekt står to ganger.' };
    seen.add(key);
    out.push({ carrier: row.carrier, maxWeightG: w, priceOre: p });
  }
  out.sort((a, b) => CARRIERS.indexOf(a.carrier) - CARRIERS.indexOf(b.carrier) || a.maxWeightG - b.maxWeightG);
  return { ok: true, value: out };
}

/** The basket's weight: each line's quantity times its product's weight (DEFAULT_WEIGHT_G when unknown). */
function weightOf(lines, products) {
  const byId = new Map((products || []).map((p) => [p.id, p]));
  let g = 0;
  for (const l of lines || []) {
    const p = byId.get(l.productId);
    const w = p && validWeightG(p.weightG) && p.weightG ? p.weightG : DEFAULT_WEIGHT_G;
    g += w * (Number(l.quantity) || 0);
  }
  return g;
}

/** The cheapest band of that carrier that takes the weight (the smallest «inntil» ≥ weight), or null: cannot be sent so. */
function rateFor(list, carrier, weightG) {
  let best = null;
  for (const r of list || []) {
    if (r.carrier !== carrier || r.maxWeightG < weightG) continue;
    if (!best || r.maxWeightG < best.maxWeightG) best = r;
  }
  return best ? { carrier, priceOre: best.priceOre, maxWeightG: best.maxWeightG } : null;
}

/** One quote per enabled carrier that can take the basket, in catalogue order: what the checkout chips show. */
function quotes(list, carriers, weightG) {
  return (carriers || []).map((c) => rateFor(list, c, weightG)).filter(Boolean);
}

/**
 * What staff give when they mark an order sent: the carrier (one the order can be sent with — any of the four), a
 * tracking number (optional for «other» or when a label will be made; when given it must look like one), and for
 * «other» optionally a tracking address. The tracking address of the three carriers is built here, never pasted.
 * `pickupPoint`: a free-text pickup point («Rema 1000 Grønland»), optional, ≤120.
 */
function shipInput(body) {
  const src = body && typeof body === 'object' && !Array.isArray(body) ? body : {};
  const errors = {};
  if (!isCarrier(src.carrier)) errors.carrier = 'Velg transportør.';
  let nr = null;
  if (src.trackingNumber !== undefined && src.trackingNumber !== null && String(src.trackingNumber).trim() !== '') {
    nr = trackingNumber(src.trackingNumber);
    if (!nr) errors.trackingNumber = 'Sporingsnummeret er 8–40 bokstaver og sifre.';
  }
  let url = null;
  if (src.carrier === 'other') {
    if (src.trackingUrl !== undefined && src.trackingUrl !== null && String(src.trackingUrl).trim() !== '') {
      url = pastedTrackingUrl(src.trackingUrl);
      if (!url) errors.trackingUrl = 'Sporingslenken må være en https-adresse på høyst 300 tegn.';
    }
  } else if (nr) url = trackingUrl(src.carrier, nr);
  const pickupPoint = clean(src.pickupPoint);
  if (pickupPoint.length > MAX_PICKUP_POINT || CTRL.test(String(src.pickupPoint || ''))) errors.pickupPoint = `Hentestedet kan ha høyst ${MAX_PICKUP_POINT} tegn.`;
  return Object.keys(errors).length ? { ok: false, errors }
    : { ok: true, value: { carrier: src.carrier, trackingNumber: nr, trackingUrl: url, pickupPoint: pickupPoint || null } };
}

/** What the buyer is shown of a sent order's shipping: the carrier's name, the number and the link — nothing else. */
function buyerShipping(o, lang = 'no') {
  if (!o || !o.carrier) return null;
  return { carrier: o.carrier, carrierLabel: label(o.carrier, lang), trackingNumber: o.tracking_number || null, trackingUrl: o.tracking_url || null,
    shippedAt: o.shipped_at || null };
}

module.exports = { CARRIERS, OFFERED, isOffered, paused, pausedCarriers, SHIPPING_MODES, LABELS, DEFAULT_WEIGHT_G, MAX_PRODUCT_WEIGHT_G, MAX_BAND_WEIGHT_G, MAX_RATE_ORE, MAX_RATES, MAX_TRACKING_URL,
  MAX_PICKUP_POINT, isCarrier, label, trackingNumber, validTrackingNumber, trackingUrl, pastedTrackingUrl, validWeightG, settings, parseCarriers, formatCarriers,
  rates, weightOf, rateFor, quotes, shipInput, buyerShipping };
